import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, PlayCircle, RefreshCw, RotateCcw, Search, X } from 'lucide-react';
import MainService from '../../services/MainService';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';
import { getAppUi } from '../../locales/appUi';
import SaAdminRoute from './SaAdminRoute';

const emptyOrder = () => ({
  Id: 0,
  VoucherNo: '',
  PaymentStatus: '',
  ReturnStatus: '',
  ClientName: '',
  CustomerName: '',
  StatusName: '',
});

const AdminOrderAdjustment = () => {
  const { language } = useLanguage();
  const ui = useMemo(() => getAppUi(language), [language]);
  const [voucherNo, setVoucherNo] = useState('');
  const [order, setOrder] = useState(emptyOrder);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 5000);
    return () => clearTimeout(timer);
  }, [success]);

  const resetPage = ({ clearMessages = true } = {}) => {
    setVoucherNo('');
    setOrder(emptyOrder());
    setLoaded(false);
    if (clearMessages) {
      setError('');
      setSuccess('');
    }
  };

  const mapOrder = (response, voucher) => ({
    Id: response.Id,
    VoucherNo: response.VoucherNo || voucher,
    PaymentStatus: response.PaymentStatus || '-',
    ReturnStatus: response.ReturnStatus || '-',
    ClientName: response.Client?.Name || '',
    CustomerName: response.Customer?.Name || '',
    StatusName: response.Status?.Name || '',
  });

  const loadOrder = async (voucher) => {
    const value = String(voucher ?? voucherNo ?? '').trim();
    if (!value) {
      setError('Enter Voucher Number');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await MainService.OrderByVoucherNo(value);
      if (!response?.Id) {
        setError('Order not found');
        return;
      }

      setOrder(mapOrder(response, value));
      setVoucherNo(response.VoucherNo || value);
      setLoaded(true);
    } catch (err) {
      console.error('Error loading order by voucher:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  const reloadCurrentOrder = async () => {
    if (!order.VoucherNo) return;
    const response = await MainService.OrderByVoucherNo(order.VoucherNo);
    if (response?.Id) {
      setOrder(mapOrder(response, order.VoucherNo));
    }
  };

  const approvePayment = async () => {
    if (!order.Id) {
      setError('Enter Voucher Number');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await MainService.UpdateOrderPaymentApproved(order.Id);
      if (response === true) {
        setSuccess('Payment status updated');
        await reloadCurrentOrder();
      } else {
        setError('Failed');
      }
    } catch (err) {
      console.error('Error approving payment:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  const approveReturn = async () => {
    if (!order.Id) {
      setError('Enter Voucher Number');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await MainService.UpdateOrderReturnApproved(order.Id);
      if (response === true) {
        setSuccess('Return status updated');
        await reloadCurrentOrder();
      } else {
        setError('Failed');
      }
    } catch (err) {
      console.error('Error approving return:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  const setOrderAsProcessing = async () => {
    if (!order.Id) {
      setError('Enter Voucher Number');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await MainService.UpdateOrderAsProcessing(order.Id);
      if (response === true) {
        setSuccess('Order set as processing');
        await reloadCurrentOrder();
      } else {
        setError('Failed');
      }
    } catch (err) {
      console.error('Error setting order as processing:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  const disabledClass =
    'w-full px-3 py-2 border border-gray-300 rounded-md text-sm disabled:bg-gray-50 disabled:text-gray-600';

  return (
    <SaAdminRoute>
      <div className="space-y-4 pb-28">
        {error && (
          <div className="fixed top-4 right-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 shadow-lg">
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button type="button" onClick={() => setError('')} className="flex-shrink-0 text-red-500 hover:text-red-700">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {success && (
          <div className="fixed top-4 right-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700 shadow-lg">
            <CheckCircle className="h-5 w-5 flex-shrink-0" />
            <span className="flex-1">{success}</span>
            <button type="button" onClick={() => setSuccess('')} className="flex-shrink-0 text-green-500 hover:text-green-700">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h1 className="mb-1 text-2xl font-bold text-gray-900">{getPageHeading(language, 'adminOrderAdjustment')}</h1>
          <p className="mb-4 text-sm text-gray-500">Enter voucher number to update payment, return, or processing status.</p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[220px] max-w-sm flex-1">
              <label className="mb-1 block text-sm font-medium text-gray-700">{ui('orders.createOrder.voucherNo')}</label>
              <input
                type="text"
                className={disabledClass}
                value={voucherNo}
                onChange={(e) => setVoucherNo(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !loaded) {
                    e.preventDefault();
                    loadOrder(voucherNo);
                  }
                }}
                disabled={loaded}
                placeholder={ui('orders.createOrder.voucherNo')}
              />
            </div>
            {!loaded && (
              <button
                type="button"
                onClick={() => loadOrder(voucherNo)}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
              >
                <Search className="h-4 w-4" />
                {ui('common.search')}
              </button>
            )}
          </div>
        </div>

        {loaded && (
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">{ui('orders.createOrder.clientName')}</label>
                <input type="text" className={disabledClass} value={order.ClientName} disabled readOnly />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">{ui('orders.createOrder.customerName')}</label>
                <input type="text" className={disabledClass} value={order.CustomerName} disabled readOnly />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">{ui('common.status')}</label>
                <input type="text" className={disabledClass} value={order.StatusName} disabled readOnly />
              </div>
            </div>

            <div className="grid max-w-2xl grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Payment Status</label>
                <input type="text" className={disabledClass} value={order.PaymentStatus} disabled readOnly />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Return Status</label>
                <input type="text" className={disabledClass} value={order.ReturnStatus} disabled readOnly />
              </div>
            </div>
          </div>
        )}

        {loaded && (
          <div className="fixed bottom-8 left-1/2 z-40 flex -translate-x-1/2 flex-wrap items-center justify-center gap-3 rounded-2xl border-2 border-gray-200 bg-white px-6 py-4 shadow-xl">
            {loading ? (
              <RefreshCw className="h-6 w-6 animate-spin text-primary" />
            ) : (
              <>
                <button
                  type="button"
                  onClick={approvePayment}
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-white shadow-sm transition-colors hover:bg-emerald-700"
                >
                  <CheckCircle className="h-4 w-4" />
                  Approve Payment
                </button>
                <button
                  type="button"
                  onClick={approveReturn}
                  className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-white shadow-sm transition-colors hover:bg-indigo-700"
                >
                  <RotateCcw className="h-4 w-4" />
                  Approve Return
                </button>
                <button
                  type="button"
                  onClick={setOrderAsProcessing}
                  className="inline-flex items-center gap-2 rounded-xl bg-sky-600 px-4 py-3 text-white shadow-sm transition-colors hover:bg-sky-700"
                >
                  <PlayCircle className="h-4 w-4" />
                  Set as Processing
                </button>
                <button
                  type="button"
                  onClick={() => resetPage()}
                  className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-3 text-white shadow-sm transition-colors hover:bg-amber-600"
                >
                  <RefreshCw className="h-4 w-4" />
                  {ui('common.clear')}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </SaAdminRoute>
  );
};

export default AdminOrderAdjustment;
